'use client';

import { c as _c } from "react/compiler-runtime";
import type { JSX, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { computeDebateSettledArtboardScale, DEBATE_SETTLED_ARTBOARD, DEBATE_SETTLED_BACKGROUND_SRC, DEBATE_SETTLED_COPY, DEBATE_SETTLED_TIMING, easeOutCubic, formatRewardAmount, getDebateSettledRestingRewardAmount, randomRewardAmount } from '@/components/support-mainnet/how-it-works/debate-settled-animation-engine';
import { useHowItWorksOrchestrationActive, useHowItWorksPlaybackGeneration, useHowItWorksPlaybackInstanceKey, useHowItWorksStepShouldPlay } from '@/components/support-mainnet/how-it-works/how-it-works-step-playback-api';
import { cn } from '@/lib/ui/utils';
type DebateSettledPlaybackState = {
  go: boolean;
  open: boolean;
  reward: boolean;
  prizeAmount: number;
  backerAmount: number;
};
function createInitialPlaybackState(): DebateSettledPlaybackState {
  return {
    go: false,
    open: false,
    reward: false,
    prizeAmount: 0,
    backerAmount: 0
  };
}
function createFinalPlaybackState(prizeAmount: number, backerAmount: number): DebateSettledPlaybackState {
  return {
    go: true,
    open: true,
    reward: true,
    prizeAmount,
    backerAmount
  };
}
function createRestingPlaybackState(): DebateSettledPlaybackState {
  const amount = getDebateSettledRestingRewardAmount();
  return createFinalPlaybackState(amount, amount);
}
function PrizePoolIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="debate-settled-chip-icon debate-settled-chip-icon-prize" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.998 17C10.3244 17 8.86776 18.265 8.11571 20.1312C7.75651 21.0225 8.27194 22 8.95682 22H15.0392C15.7241 22 16.2395 21.0225 15.8803 20.1312C15.1283 18.265 13.6716 17 11.998 17Z" stroke="currentColor" strokeWidth="2.11736" strokeLinecap="round" /><path d="M18.502 5H19.7042C20.9051 5 21.5055 5 21.8188 5.37736C22.132 5.75472 22.0018 6.32113 21.7413 7.45395L21.3505 9.15307C20.7629 11.7086 18.6129 13.6088 16.002 14" stroke="currentColor" strokeWidth="2.11736" strokeLinecap="round" strokeLinejoin="round" /><path d="M5.49805 4.99902H4.29584C3.09497 4.99902 2.49454 4.99902 2.18129 5.37638C1.86804 5.75374 1.99829 6.32015 2.2588 7.45297L2.64953 9.15209C3.23719 11.7076 5.38717 13.6078 7.99805 13.999" stroke="currentColor" strokeWidth="2.11736" strokeLinecap="round" strokeLinejoin="round" /><path d="M12.002 17.001C15.0228 17.001 17.567 12.3389 18.3317 5.99187C18.5432 4.23656 18.649 3.35891 18.0888 2.67994C17.5287 2.00098 16.6243 2.00098 14.8154 2.00098H9.18853C7.3797 2.00098 6.47528 2.00098 5.91512 2.67994C5.35496 3.35891 5.4607 4.23656 5.6722 5.99187C6.43695 12.3389 8.98118 17.001 12.002 17.001Z" stroke="currentColor" strokeWidth="2.11736" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BackerRewardsIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="debate-settled-chip-icon debate-settled-chip-icon-backer" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4.00195 11V15C4.00195 18.2998 4.00195 19.9497 5.02708 20.9749C6.0522 22 7.70212 22 11.002 22H13.002C16.3018 22 17.9517 22 18.9769 20.9749C20.002 19.9497 20.002 18.2998 20.002 15V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M3 8.99902C3 8.25133 3 7.87748 3.20096 7.59902C3.33261 7.4166 3.52197 7.26511 3.75 7.15979C4.09808 6.99902 4.56538 6.99902 5.5 6.99902H18.5C19.4346 6.99902 19.9019 6.99902 20.25 7.15979C20.478 7.26511 20.6674 7.4166 20.799 7.59902C21 7.87748 21 8.25133 21 8.99902C21 9.74671 21 10.1205 20.799 10.399C20.6674 10.5814 20.478 10.7329 20.25 10.8382C19.9019 10.999 19.4346 10.999 18.5 10.999H5.5C4.56538 10.999 4.09808 10.999 3.75 10.8382C3.52197 10.7329 3.33261 10.5814 3.20096 10.399C3 10.1205 3 9.74671 3 8.99902Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M6 3.78669C6 2.80047 6.79949 2.00098 7.78571 2.00098H8.14286C10.2731 2.00098 12 3.72788 12 5.85812V7.00098H9.21429C7.43908 7.00098 6 5.56189 6 3.78669Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M18 3.78669C18 2.80047 17.2005 2.00098 16.2143 2.00098H15.8571C13.7269 2.00098 12 3.72788 12 5.85812V7.00098H14.7857C16.5609 7.00098 18 5.56189 18 3.78669Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M12 11.001V22.001" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function OutcomeSealIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="debate-settled-chip-icon-seal-check" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M31.4786 37.2515C32.9933 37.2515 35.1531 37.5458 36.3449 36.364C37.5462 35.1728 37.2505 33.004 37.2505 31.4796C37.2505 29.5533 37.6717 28.6676 39.0436 27.2957C41.0844 25.2551 42.1047 24.2346 42.1047 22.9669C42.1047 21.6989 41.0844 20.6787 39.0436 18.638C37.6719 17.2662 37.2505 16.3804 37.2505 14.4541C37.2505 12.9297 37.5462 10.761 36.345 9.56975C35.1533 8.38788 32.9933 8.68221 31.4786 8.68221C29.7324 8.68221 28.519 8.11343 27.2947 6.88912C25.2541 4.84845 24.2338 3.82812 22.9659 3.82812C21.698 3.82812 20.6777 4.84839 18.6372 6.88894L18.637 6.88912C17.4125 8.11368 16.199 8.68221 14.4531 8.68221C12.9288 8.68221 10.76 8.38656 9.56878 9.58782C8.38694 10.7796 8.68127 12.9394 8.68127 14.4541C8.68127 16.2 8.1127 17.4134 6.88811 18.638L6.88802 18.6381C4.84745 20.6787 3.82717 21.699 3.82715 22.9669C3.82719 24.2346 4.84751 25.2551 6.88816 27.2957C8.25999 28.6676 8.68127 29.5533 8.68127 31.4796C8.68127 33.004 8.38564 35.1728 9.58694 36.364C10.7787 37.5458 12.9385 37.2515 14.4531 37.2515C16.3123 37.2515 17.2076 37.6151 18.5345 38.942C19.6643 40.072 21.1789 42.1056 22.9659 42.1056C24.7529 42.1056 26.2675 40.072 27.3973 38.942C28.7242 37.6151 29.6195 37.2515 31.4786 37.2515Z" fill="white" /><path d="M36.3449 36.364H36.363M36.3449 36.364C35.1531 37.5458 32.9933 37.2515 31.4786 37.2515C29.6195 37.2515 28.7242 37.6151 27.3973 38.942C26.2675 40.072 24.7529 42.1056 22.9659 42.1056C21.1789 42.1056 19.6643 40.072 18.5345 38.942C17.2076 37.6151 16.3123 37.2515 14.4531 37.2515C12.9385 37.2515 10.7787 37.5458 9.58694 36.364C8.38564 35.1728 8.68127 33.004 8.68127 31.4796C8.68127 29.5533 8.25999 28.6676 6.88816 27.2957C4.84751 25.2551 3.82719 24.2346 3.82715 22.9669C3.82717 21.6989 4.84747 20.6787 6.88811 18.638C8.1127 17.4134 8.68127 16.2 8.68127 14.4541C8.68127 12.9394 8.38694 10.7796 9.56878 9.58782C10.76 8.38656 12.9288 8.68221 14.4531 8.68221C16.199 8.68221 17.4125 8.11368 18.637 6.88912C20.6777 4.84845 21.698 3.82812 22.9659 3.82812C24.2338 3.82812 25.2541 4.84845 27.2947 6.88912C28.519 8.11343 29.7324 8.68221 31.4786 8.68221C32.9933 8.68221 35.1533 8.38788 36.345 9.56975C37.5462 10.761 37.2505 12.9297 37.2505 14.4541C37.2505 16.3804 37.6719 17.2662 39.0436 18.638C41.0844 20.6787 42.1047 21.6989 42.1047 22.9669C42.1047 24.2346 41.0844 25.2551 39.0436 27.2957C37.6717 28.6676 37.2505 29.5533 37.2505 31.4796C37.2505 33.004 37.5462 35.1728 36.3449 36.364Z" stroke="white" strokeWidth="4.10116" /><path d="M17.2246 24.6752C17.2246 24.6752 19.5213 25.9227 20.6696 27.751C20.6696 27.751 24.1146 20.574 28.7079 18.1816" stroke="currentColor" strokeWidth="4.10116" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function useDebateSettledPlayback(rootRef) {
  const $ = _c(10);
  const [state, setState] = useState(createInitialPlaybackState);
  const playedRef = useRef(false);
  const orchestrationActive = useHowItWorksOrchestrationActive();
  const shouldPlay = useHowItWorksStepShouldPlay(2);
  const playbackGeneration = useHowItWorksPlaybackGeneration(2);
  const playbackEnabled = orchestrationActive === false || shouldPlay;
  let t0;
  if ($[0] !== orchestrationActive || $[1] !== playbackEnabled || $[2] !== rootRef) {
    t0 = () => {
      if (!playbackEnabled) {
        return;
      }
      const root = rootRef.current;
      if (root === null) {
        return;
      }
      playedRef.current = false;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      let timerIds = [];
      let prizeFrameId = null;
      let backerFrameId = null;
      const at = (delayMs, run) => {
        timerIds = [...timerIds, window.setTimeout(run, delayMs)];
      };
      const cleanup = () => {
        for (const timer of timerIds) {
          window.clearTimeout(timer);
        }
        timerIds = [];
        if (prizeFrameId !== null) {
          window.cancelAnimationFrame(prizeFrameId);
          prizeFrameId = null;
        }
        if (backerFrameId !== null) {
          window.cancelAnimationFrame(backerFrameId);
          backerFrameId = null;
        }
      };
      const countUp = _temp;
      const play = () => {
        if (playedRef.current) {
          return;
        }
        playedRef.current = true;
        const prizeTarget = randomRewardAmount();
        const backerTarget = randomRewardAmount();
        if (reducedMotion) {
          setState(createFinalPlaybackState(prizeTarget, backerTarget));
          return;
        }
        setState(createInitialPlaybackState());
        at(DEBATE_SETTLED_TIMING.pillPopMs, () => {
          setState(_temp2);
        });
        at(DEBATE_SETTLED_TIMING.pillOpenMs, () => {
          setState(_temp3);
        });
        at(DEBATE_SETTLED_TIMING.rewardMs, () => {
          setState(_temp4);
          countUp(prizeTarget, value => {
            setState(previous_2 => ({
              ...previous_2,
              prizeAmount: value
            }));
          }, id => {
            prizeFrameId = id;
          });
          countUp(backerTarget, value_0 => {
            setState(previous_3 => ({
              ...previous_3,
              backerAmount: value_0
            }));
          }, id_0 => {
            backerFrameId = id_0;
          });
        });
      };
      if (orchestrationActive) {
        const startTimer = window.setTimeout(() => {
          play();
        }, 0);
        return () => {
          window.clearTimeout(startTimer);
          cleanup();
        };
      }
      if (typeof IntersectionObserver === "undefined") {
        const startTimer_0 = window.setTimeout(() => {
          play();
        }, 0);
        return () => {
          window.clearTimeout(startTimer_0);
          cleanup();
        };
      }
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target === root && entry.isIntersecting) {
            play();
          }
        }
      }, {
        threshold: DEBATE_SETTLED_TIMING.intersectionThreshold
      });
      observer.observe(root);
      return () => {
        observer.disconnect();
        cleanup();
      };
    };
    $[0] = orchestrationActive;
    $[1] = playbackEnabled;
    $[2] = rootRef;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== orchestrationActive || $[5] !== playbackEnabled || $[6] !== playbackGeneration || $[7] !== rootRef) {
    t1 = [playbackEnabled, orchestrationActive, playbackGeneration, rootRef];
    $[4] = orchestrationActive;
    $[5] = playbackEnabled;
    $[6] = playbackGeneration;
    $[7] = rootRef;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
  if (!playbackEnabled) {
    let t2;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = createRestingPlaybackState();
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  return state;
}
function _temp4(previous_1) {
  return {
    ...previous_1,
    reward: true
  };
}
function _temp3(previous_0) {
  return {
    ...previous_0,
    open: true
  };
}
function _temp2(previous) {
  return {
    ...previous,
    go: true
  };
}
function _temp(target, apply, onFrameId) {
  const startedAt = performance.now();
  const durationMs = DEBATE_SETTLED_TIMING.countUpDurationMs;
  const frame = now => {
    const progress = Math.min((now - startedAt) / durationMs, 1);
    apply(target * easeOutCubic(progress));
    if (progress < 1) {
      onFrameId(window.requestAnimationFrame(frame));
      return;
    }
    onFrameId(null);
  };
  onFrameId(window.requestAnimationFrame(frame));
}
function useArtboardScale(wrapRef) {
  const $ = _c(3);
  const [scale, setScale] = useState(1);
  let t0;
  let t1;
  if ($[0] !== wrapRef) {
    t0 = () => {
      const wrap = wrapRef.current;
      if (wrap === null) {
        return;
      }
      const updateScale = () => {
        const {
          width,
          height
        } = wrap.getBoundingClientRect();
        setScale(computeDebateSettledArtboardScale(width, height));
      };
      updateScale();
      const observer = new ResizeObserver(updateScale);
      observer.observe(wrap);
      return () => {
        observer.disconnect();
      };
    };
    t1 = [wrapRef];
    $[0] = wrapRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return scale;
}
export function DebateSettledStepAnimation() {
  const $ = _c(2);
  const instanceKey = useHowItWorksPlaybackInstanceKey(2);
  let t0;
  if ($[0] !== instanceKey) {
    t0 = <DebateSettledStepAnimationBody key={instanceKey} />;
    $[0] = instanceKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function DebateSettledStepAnimationBody() {
  const $ = _c(36);
  const rootRef = useRef(null);
  const wrapRef = useRef(null);
  const scale = useArtboardScale(wrapRef);
  const playback = useDebateSettledPlayback(rootRef);
  const slotWidth = DEBATE_SETTLED_ARTBOARD.width * scale;
  const slotHeight = DEBATE_SETTLED_ARTBOARD.height * scale;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden="true" className="debate-settled-bg" data-testid="debate-settled-background"><img alt="" className="debate-settled-bg-img" src={DEBATE_SETTLED_BACKGROUND_SRC} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== slotHeight || $[2] !== slotWidth) {
    t1 = {
      width: slotWidth,
      height: slotHeight
    };
    $[1] = slotHeight;
    $[2] = slotWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `scale(${scale})`;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      transform: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = playback.go && "debate-settled-go";
  const t5 = playback.open && "debate-settled-open";
  const t6 = playback.reward && "debate-settled-reward";
  let t7;
  if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
    t7 = cn("debate-settled-card", t4, t5, t6);
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t10;
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PrizePoolIcon />;
    t9 = <span className="debate-settled-chip-label">{DEBATE_SETTLED_COPY.prizeLabel}</span>;
    t10 = <br />;
    $[10] = t10;
    $[11] = t8;
    $[12] = t9;
  } else {
    t10 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] !== playback.prizeAmount) {
    t11 = formatRewardAmount(playback.prizeAmount);
    $[13] = playback.prizeAmount;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11) {
    t12 = <div className="debate-settled-chip debate-settled-chip-prize">{t8}<span>{t9}{t10}<span className="debate-settled-chip-value" data-testid="debate-settled-prize-value">{t11}</span></span></div>;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  let t14;
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <BackerRewardsIcon />;
    t14 = <span className="debate-settled-chip-label">{DEBATE_SETTLED_COPY.backerLabel}</span>;
    t15 = <br />;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
  } else {
    t13 = $[17];
    t14 = $[18];
    t15 = $[19];
  }
  let t16;
  if ($[20] !== playback.backerAmount) {
    t16 = formatRewardAmount(playback.backerAmount);
    $[20] = playback.backerAmount;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t16) {
    t17 = <div className="debate-settled-chip debate-settled-chip-backer">{t13}<span>{t14}{t15}<span className="debate-settled-chip-value" data-testid="debate-settled-backer-value">{t16}</span></span></div>;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="debate-settled-pill-text">{DEBATE_SETTLED_COPY.outcomeLabel}</span>;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="debate-settled-pill">{t18}<span className="debate-settled-seal"><OutcomeSealIcon /></span></div>;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t12 || $[27] !== t17 || $[28] !== t7) {
    t20 = <div className={t7}>{t12}{t17}{t19}</div>;
    $[26] = t12;
    $[27] = t17;
    $[28] = t7;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t20 || $[31] !== t3) {
    t21 = <div className="debate-settled-scale" style={t3}>{t20}</div>;
    $[30] = t20;
    $[31] = t3;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t1 || $[34] !== t21) {
    t22 = <div ref={rootRef} aria-hidden="true" className="debate-settled relative h-full w-full" data-testid="debate-settled-animation">{t0}<div ref={wrapRef} className="debate-settled-wrap"><div className="debate-settled-slot" style={t1}>{t21}</div></div></div>;
    $[33] = t1;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  return t22;
}